import axios from 'axios'

class TranscriptSyncAPI {
  constructor() {
    this.baseURL = '/transcript-sync/api'
    this.ssoURL = '/transcript-sync/api/sso'
    
    // Create axios instance
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      config => {
        const token = localStorage.getItem('transcript_sync_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      error => Promise.reject(error)
    )

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          // Handle unauthorized - clear token and redirect to login
          localStorage.removeItem('transcript_sync_token')
          localStorage.removeItem('transcript_sync_user')
          window.location.href = '/transcript-sync/auth/sso'
        }
        return Promise.reject(error)
      }
    )
  }

  // SSO Authentication
  async decryptSSO(encryptedData) {
    try {
      const response = await axios.post(`${this.baseURL}/sso/decrypt`, {
        encryptedData: encryptedData
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        }
      })
      return response.data
    } catch (error) {
      console.error('SSO Decrypt error:', error)
      throw error
    }
  }

  // Authentication
  async login(credentials) {
    const response = await this.client.post('/auth/login', credentials)
    return response.data
  }

  async logout() {
    try {
      await this.client.post('/auth/logout')
    } catch (error) {
      console.warn('Logout request failed:', error)
    }
    localStorage.removeItem('transcript_sync_token')
    localStorage.removeItem('transcript_sync_user')
  }

  async getUser() {
    const response = await this.client.get('/auth/user')
    return response.data
  }

  // Dashboard & Stats
  async getStats(locationId = null) {
    const params = locationId ? { location_id: locationId } : {}
    const response = await this.client.get('/stats', { params })
    return response.data
  }
  
  // New Dashboard APIs
  async getDashboardStats() {
    const response = await this.client.get('/dashboard/stats')
    return response.data
  }
  
  async getDashboardIntegrations() {
    const response = await this.client.get('/dashboard/integrations')
    return response.data
  }
  
  async initializePlatform(platform) {
    const response = await this.client.post('/dashboard/initialize-platform', {
      platform: platform
    })
    return response.data
  }
  
  async getDashboardSyncEvents(params = {}) {
    const response = await this.client.get('/dashboard/sync-events', { params })
    return response.data
  }
  
  async getSyncEventDetails(id) {
    const response = await this.client.get(`/dashboard/sync-events/${id}`)
    return response.data
  }
  
  async retrySyncEvent(id) {
    const response = await this.client.post(`/dashboard/sync-events/${id}/retry`)
    return response.data
  }

  async getDashboardData(locationId = null) {
    try {
      const [stats, integrations, syncEvents] = await Promise.all([
        this.getDashboardStats(),
        this.getDashboardIntegrations(),
        this.getDashboardSyncEvents({ per_page: 20 })
      ])
      
      return {
        stats: stats.data || stats,
        integrations: integrations.data || integrations,
        syncEvents: syncEvents.data || syncEvents
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      throw error
    }
  }

  // Integrations
  async getIntegrations(locationId = null) {
    const params = locationId ? { location_id: locationId } : {}
    const response = await this.client.get('/integrations', { params })
    return response.data
  }

  async createIntegration(data) {
    const response = await this.client.post('/integrations', data)
    return response.data
  }

  async updateIntegration(id, data) {
    const response = await this.client.put(`/integrations/${id}`, data)
    return response.data
  }

  async deleteIntegration(id) {
    await this.client.delete(`/integrations/${id}`)
  }

  async testConnection(id) {
    const response = await this.client.post(`/integrations/${id}/test`)
    return response.data
  }

  async toggleIntegration(id) {
    const response = await this.client.post(`/integrations/${id}/toggle`)
    return response.data
  }

  // Sync History
  async getSyncHistory(locationId = null, limit = 50) {
    const params = { limit }
    if (locationId) params.location_id = locationId
    
    const response = await this.client.get('/sync-history', { params })
    return response.data
  }

  async getSyncLog(logId) {
    const response = await this.client.get(`/sync-history/${logId}`)
    return response.data
  }

  async retrySyncLog(logId) {
    const response = await this.client.post(`/sync-history/${logId}/retry`)
    return response.data
  }

  async deleteSyncLog(logId) {
    await this.client.delete(`/sync-history/${logId}`)
  }

  // Providers
  async getProviders() {
    const response = await this.client.get('/providers')
    return response.data
  }

  async getProviderConfig(provider) {
    const response = await this.client.get(`/providers/${provider}/config`)
    return response.data
  }

  async validateProviderConfig(provider, config) {
    const response = await this.client.post(`/providers/${provider}/validate`, config)
    return response.data
  }

  // Quick Actions (for iframe)
  async quickToggle(integrationId) {
    const response = await axios.post(`/transcript-sync/api/quick-toggle/${integrationId}`, {}, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('transcript_sync_token')}`,
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      }
    })
    return response.data
  }

  // Utility methods
  getAuthToken() {
    return localStorage.getItem('transcript_sync_token')
  }

  setAuthToken(token) {
    if (token) {
      localStorage.setItem('transcript_sync_token', token)
    } else {
      localStorage.removeItem('transcript_sync_token')
    }
  }

  isAuthenticated() {
    return !!this.getAuthToken()
  }

  // Provider helpers
  getProviderIcon(provider) {
    const icons = {
      'fireflies': 'fire',
      'phantom': 'ghost', 
      'otter': 'otter-spotter',
      'rev': 'microphone',
      'trint': 'file-text'
    }
    return icons[provider] || 'microphone'
  }

  getProviderName(provider) {
    const names = {
      'fireflies': 'Fireflies.ai',
      'phantom': 'Phantom',
      'otter': 'Otter.ai',
      'rev': 'Rev.com',
      'trint': 'Trint'
    }
    return names[provider] || provider.charAt(0).toUpperCase() + provider.slice(1)
  }

  getStatusBadgeClass(status) {
    const classes = {
      'success': 'badge-success',
      'pending': 'badge-warning', 
      'failed': 'badge-danger',
      'processing': 'badge-info'
    }
    return classes[status] || 'badge-secondary'
  }

  formatDate(dateString) {
    if (!dateString) return '--'
    
    try {
      const date = new Date(dateString)
      return date.toLocaleString()
    } catch (error) {
      return dateString
    }
  }

  formatRelativeTime(dateString) {
    if (!dateString) return '--'
    
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffInSeconds = Math.floor((now - date) / 1000)
      
      if (diffInSeconds < 60) return 'Just now'
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
      return `${Math.floor(diffInSeconds / 86400)}d ago`
    } catch (error) {
      return dateString
    }
  }
}

export default new TranscriptSyncAPI()